import { useChart } from 'uikit';
import { formatNumberToRu } from 'utils';

// eslint-disable-next-line import/no-extraneous-dependencies
import { Bar } from '@antv/g2plot';
import type React from 'react';
import { useMemo, useRef } from 'react';

export interface IGroupedBarItem {
    value: number;
    label: string;
    type: string;
}

interface IGroupedBar {
    className?: string;
    items?: IGroupedBarItem[];
    color?: string[];
    height?: number;
    itemsToFitHeight?: number;
    barWidthWithoutScroll?: number;
}

const tooltipColor = '#EEF8FB';

export const GroupedBar: React.FC<IGroupedBar> = (props) => {
    const { className, items, color, height, itemsToFitHeight = 5, barWidthWithoutScroll } = props;

    const xAxisValuesRef = useRef<number[]>([]);

    const minBarWidth = useMemo(
        () => ((items?.length || 0) / 2 < itemsToFitHeight ? barWidthWithoutScroll : undefined),
        [barWidthWithoutScroll, items?.length, itemsToFitHeight],
    );

    const { container } = useChart(Bar, {
        data: items,
        isGroup: true,
        xField: 'value',
        yField: 'label',
        seriesField: 'type',
        marginRatio: 0,
        color,
        legend: false,
        dodgePadding: 4,
        yAxis: {
            label: {
                style: {
                    fill: 'rgb(0, 32, 51)',
                    fontSize: 14,
                    fontWeight: 600,
                },
            },
        },
        xAxis: {
            label: {
                style: {
                    fontSize: 12,
                    fontWeight: 400,
                },
                formatter: (v) => {
                    const xAxisVal = Number(v);

                    xAxisValuesRef.current.push(xAxisVal);

                    return formatNumberToRu(xAxisVal, 0);
                },
            },
        },
        label: {
            position: (data) => {
                const currentValue = data.value;
                const isLessThanZero = currentValue < 0;
                const xLabelsLen = xAxisValuesRef.current.length;
                let isBetweenBeforeLastAndLast = false;

                if (xLabelsLen >= 2) {
                    if (!isLessThanZero) {
                        isBetweenBeforeLastAndLast =
                            currentValue >= xAxisValuesRef.current[xLabelsLen - 2] &&
                            currentValue < xAxisValuesRef.current[xLabelsLen - 1];
                    } else {
                        isBetweenBeforeLastAndLast =
                            currentValue > xAxisValuesRef.current[0] && currentValue < xAxisValuesRef.current[1];
                    }
                }

                return isBetweenBeforeLastAndLast ? 'middle' : 'right';
            },
            formatter: (v) => formatNumberToRu(v.value, 0),
            style: {
                fill: 'rgb(0, 32, 51)',
                fontSize: 14,
                fontWeight: 600,
            },
        },
        barStyle: {
            radius: [2, 2, 0, 0],
        },
        tooltip: {
            formatter: (data: any) => ({
                name: data.type,
                value: `${formatNumberToRu(Number(data.value), 0)}`,
            }),
            enterable: true,
            domStyles: {
                'g2-tooltip': {
                    background: '#002033',
                },
                'g2-tooltip-name': {
                    color: tooltipColor,
                },
                'g2-tooltip-value': {
                    color: tooltipColor,
                    fontWeight: 700,
                },
                'g2-tooltip-title': {
                    color: tooltipColor,
                    fontWeight: 700,
                },
            },
        },
        height,
        scrollbar:
            (items?.length || 0) / 2 > itemsToFitHeight
                ? {
                      type: 'vertical',
                      animate: false,
                      height,
                  }
                : undefined,
        minBarWidth,
    });

    return (
        <div className={className}>
            <div ref={container} />
        </div>
    );
};

GroupedBar.displayName = 'GroupedBar';
